import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api'
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://bee.share-it-up.com'

// ─── Types ────────────────────────────────────────────────────────────────────

type ReportType = 'varroa' | 'nosema' | 'general' | 'loss_summary'
type ReportStatus = 'draft' | 'pending_signature' | 'signed' | 'rejected'

interface Report {
  _id: string
  type: ReportType
  title: string
  programYear: number
  date: string
  yardNames: string[]
  userName: string
  status: ReportStatus
  signedAt?: string
  signerName?: string
  signerLastName?: string
  signerRole?: string
  signatureDataUrl?: string
  varroaData?: any
  nosemaData?: any
  generalData?: any
}

// ─── Styled ───────────────────────────────────────────────────────────────────

const Page = styled.div`padding: 24px; max-width: 1100px; margin: 0 auto;`
const Header = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;`
const Title = styled.h1`font-size: 24px; font-weight: 700; display: flex; align-items: center; gap: 10px;`
const Sub = styled.p`color: #6b7280; font-size: 14px; margin-top: 2px;`

const Toolbar = styled.div`display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; align-items: center;`
const Select = styled.select`padding: 8px 12px; border-radius: 8px; border: 1px solid #e5e7eb; font-size: 14px; background: #fff; cursor: pointer;`
const Btn = styled.button<{ variant?: string }>`
  padding: 9px 18px; border-radius: 8px; border: none; cursor: pointer; font-size: 13px; font-weight: 600;
  background: ${p => p.variant === 'danger' ? '#fee2e2' : p.variant === 'success' ? '#d1fae5' : p.variant === 'ghost' ? '#f3f4f6' : '#6366f1'};
  color: ${p => p.variant === 'danger' ? '#ef4444' : p.variant === 'success' ? '#059669' : p.variant === 'ghost' ? '#374151' : '#fff'};
  &:hover { opacity: 0.85; }
`

const Table = styled.table`width: 100%; border-collapse: collapse; background: #fff; border-radius: 8px; overflow: hidden; border: 1px solid #E6DCC8;`
const Th = styled.th`text-align: start; padding: 12px 16px; background: #F3EBDB; color: #2B1A0F; font-size: 14px; font-weight: 700; border-bottom: 2px solid #D9C9A8; white-space: nowrap;`
const Td = styled.td`padding: 12px 16px; border-bottom: 1px solid #EDE4D3; font-size: 14px; vertical-align: middle;`
const Tr = styled.tr`cursor: pointer; &:nth-of-type(even) { background: #FBF6EC; } &:hover { background: #F6EEDD; } &:last-child td { border-bottom: none; }`

const Badge = styled.span<{ color: string }>`
  padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700;
  background: ${p => p.color}18; color: ${p => p.color}; border: 1px solid ${p => p.color}33;
`

const Empty = styled.div`text-align: center; padding: 60px; color: #9ca3af; font-size: 15px;`

// Modal
const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 16px;`
const ModalBox = styled.div`background: #fff; border-radius: 14px; width: 100%; max-width: 700px; max-height: 90vh; overflow-y: auto;`
const ModalHeader = styled.div`padding: 20px 24px 16px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: flex-start;`
const ModalBody = styled.div`padding: 20px 24px;`
const ModalTitle = styled.h2`font-size: 20px; font-weight: 700;`
const CloseBtn = styled.button`background: none; border: none; font-size: 24px; cursor: pointer; color: #9ca3af; line-height: 1;`

const Row = styled.div`display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f3f4f6; font-size: 14px;`
const RL = styled.span`color: #6b7280; font-weight: 500;`
const RV = styled.span`font-weight: 600; text-align: right;`

const DataBlock = styled.div`background: #f9fafb; border-radius: 10px; padding: 14px; margin: 14px 0; font-size: 14px; line-height: 1.7;`
const SigImg = styled.img`border: 1px solid #e5e7eb; border-radius: 8px; max-width: 100%; margin-top: 8px;`

const CopyBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 8px;
  border: 1px solid #6366f1; background: #eef2ff; color: #6366f1; font-size: 13px;
  font-weight: 600; cursor: pointer; &:hover { background: #e0e7ff; }
`

// Form
const FormCard = styled.div`background: #fff; border-radius: 14px; padding: 24px; border: 1px solid #e5e7eb; margin-bottom: 24px;`
const FormTitle = styled.h2`font-size: 18px; font-weight: 700; margin-bottom: 16px;`
const Label = styled.label`display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 4px; margin-top: 12px;`
const Input = styled.input`width: 100%; padding: 9px 12px; border-radius: 8px; border: 1px solid #d1d5db; font-size: 14px; outline: none; &:focus { border-color: #6366f1; }`
const Textarea = styled.textarea`width: 100%; padding: 9px 12px; border-radius: 8px; border: 1px solid #d1d5db; font-size: 14px; outline: none; min-height: 80px; resize: vertical; &:focus { border-color: #6366f1; }`

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Labels are translated at render time: reports.types.<key> / reports.statuses.<key>
const TYPE_META: Record<ReportType, { color: string; emoji: string }> = {
  varroa:       { color: '#f59e0b', emoji: '🔬' },
  nosema:       { color: '#8b5cf6', emoji: '🧫' },
  general:      { color: '#3b82f6', emoji: '📋' },
  loss_summary: { color: '#ef4444', emoji: '📉' },
}

const STATUS_META: Record<ReportStatus, { color: string }> = {
  draft:              { color: '#9ca3af' },
  pending_signature:  { color: '#f59e0b' },
  signed:             { color: '#10b981' },
  rejected:           { color: '#ef4444' },
}

const VARROA_METHODS = ['alcohol_wash', 'sticky_board', 'sugar_roll']

// Date-only strings ("YYYY-MM-DD") are parsed as local dates so they don't shift a day across time zones.
function fmt(d: string, locale: string) {
  if (!d) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d)
  const date = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d)
  if (isNaN(date.getTime())) return d
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' })
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ReportsScreen() {
  const { t, i18n } = useTranslation()
  const typeLabel = (k: ReportType) => t(`reports.types.${k}`)
  const statusLabel = (k: ReportStatus) => t(`reports.statuses.${k}`)
  const fmtDate = (d: string) => fmt(d, i18n.language)
  const methodLabel = (m?: string) => m ? (VARROA_METHODS.includes(m) ? t(`reports.methods.${m}`) : m.replace(/_/g, ' ')) : '—'
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()))
  const [selected, setSelected] = useState<Report | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [copied, setCopied] = useState(false)

  // Create form state
  const [form, setForm] = useState({
    type: 'varroa' as ReportType,
    title: '',
    date: new Date().toISOString().split('T')[0],
    programYear: new Date().getFullYear(),
    yardNames: '',
    // Varroa
    varroaMethod: 'alcohol_wash',
    varroaSamples: '',
    varroaAvgPct: '',
    varroaThreshold: '2',
    varroaNotes: '',
    // General
    generalFindings: '',
    generalRecs: '',
    generalNotes: '',
  })

  useEffect(() => { loadReports() }, [filterType, filterStatus, filterYear])

  async function loadReports() {
    try {
      setLoading(true)
      const token = localStorage.getItem('auth_token')
      const params: any = {}
      if (filterType)   params.type   = filterType
      if (filterStatus) params.status = filterStatus
      if (filterYear)   params.year   = filterYear
      const res = await axios.get(`${API}/reports`, { headers: { Authorization: `Bearer ${token}` }, params })
      setReports(res.data || [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  async function createReport() {
    try {
      const token = localStorage.getItem('auth_token')
      const yardNames = form.yardNames.split(',').map(s => s.trim()).filter(Boolean)
      const body: any = {
        type: form.type,
        title: form.title || t('reports.defaultTitle', { type: typeLabel(form.type), date: fmtDate(form.date) }),
        date: form.date,
        programYear: form.programYear,
        yardNames,
      }
      if (form.type === 'varroa') {
        const avg = parseFloat(form.varroaAvgPct) || 0
        const threshold = parseFloat(form.varroaThreshold) || 2
        body.varroaData = {
          method: form.varroaMethod,
          samplesCount: parseInt(form.varroaSamples) || 0,
          averageInfestationPct: avg,
          threshold,
          aboveThreshold: avg > threshold,
          notes: form.varroaNotes,
        }
      }
      if (form.type === 'general') {
        body.generalData = {
          findings: form.generalFindings,
          recommendations: form.generalRecs,
          notes: form.generalNotes,
        }
      }
      await axios.post(`${API}/reports`, body, { headers: { Authorization: `Bearer ${token}` } })
      setShowCreate(false)
      loadReports()
    } catch (e) {
      console.error(e)
    }
  }

  async function requestSignature(id: string, e: React.MouseEvent) {
    e.stopPropagation()
    try {
      const token = localStorage.getItem('auth_token')
      const res = await axios.post(`${API}/reports/${id}/request-signature`, {}, { headers: { Authorization: `Bearer ${token}` } })
      const { signUrl } = res.data
      await navigator.clipboard.writeText(signUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
      loadReports()
    } catch (e) {
      console.error(e)
    }
  }

  async function deleteReport(id: string, e: React.MouseEvent) {
    e.stopPropagation()
    if (!window.confirm(t('reports.confirmDelete'))) return
    const token = localStorage.getItem('auth_token')
    await axios.delete(`${API}/reports/${id}`, { headers: { Authorization: `Bearer ${token}` } })
    loadReports()
  }

  async function openReport(r: Report) {
    // Fetch full report (with signature)
    try {
      const token = localStorage.getItem('auth_token')
      const res = await axios.get(`${API}/reports/${r._id}`, { headers: { Authorization: `Bearer ${token}` } })
      setSelected(res.data)
    } catch { setSelected(r) }
  }

  function copySignLink(r: Report, e: React.MouseEvent) {
    e.stopPropagation()
    const url = `${BASE_URL}/sign/${(r as any).signToken || ''}`
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 3000)
  }

  return (
    <Page>
      <Header>
        <div>
          <Title>📄 {t('reports.title')}</Title>
          <Sub>{t('reports.subtitle')}</Sub>
        </div>
        <Btn onClick={() => setShowCreate(true)}>+ {t('reports.newReport')}</Btn>
      </Header>

      {copied && (
        <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontWeight: 600 }}>
          ✅ {t('reports.linkCopied')}
        </div>
      )}

      {/* ── Create Form ── */}
      {showCreate && (
        <FormCard>
          <FormTitle>{t('reports.newReport')}</FormTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <Label>{t('form.label.type')}</Label>
              <Select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value as ReportType }))} style={{ width: '100%' }}>
                {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.emoji} {typeLabel(k as ReportType)}</option>)}
              </Select>
            </div>
            <div>
              <Label>{t('form.label.date')}</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
          </div>
          <Label>{t('reports.form.titleLabel')}</Label>
          <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={t('reports.form.titlePlaceholder')} />
          <Label>{t('reports.form.yardsLabel')}</Label>
          <Input value={form.yardNames} onChange={e => setForm(f => ({ ...f, yardNames: e.target.value }))} placeholder={t('reports.form.yardsPlaceholder')} />

          {form.type === 'varroa' && <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <Label>{t('reports.form.method')}</Label>
                <Select value={form.varroaMethod} onChange={e => setForm(f => ({ ...f, varroaMethod: e.target.value }))} style={{ width: '100%' }}>
                  {VARROA_METHODS.map(m => <option key={m} value={m}>{t(`reports.methods.${m}`)}</option>)}
                </Select>
              </div>
              <div><Label>{t('reports.form.samples')}</Label><Input type="number" value={form.varroaSamples} onChange={e => setForm(f => ({ ...f, varroaSamples: e.target.value }))} placeholder="0" /></div>
              <div><Label>{t('reports.form.avgInfestation')}</Label><Input type="number" step="0.1" value={form.varroaAvgPct} onChange={e => setForm(f => ({ ...f, varroaAvgPct: e.target.value }))} placeholder="0.0" /></div>
              <div><Label>{t('reports.form.thresholdPct')}</Label><Input type="number" step="0.1" value={form.varroaThreshold} onChange={e => setForm(f => ({ ...f, varroaThreshold: e.target.value }))} /></div>
            </div>
            <Label>{t('form.label.notes')}</Label>
            <Textarea value={form.varroaNotes} onChange={e => setForm(f => ({ ...f, varroaNotes: e.target.value }))} />
          </>}

          {form.type === 'general' && <>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 4 }}>
              💡 {t('reports.form.vetHint')}
            </div>
            <Label>{t('reports.form.findingsOptional')}</Label>
            <Textarea value={form.generalFindings} onChange={e => setForm(f => ({ ...f, generalFindings: e.target.value }))} />
            <Label>{t('reports.form.recommendationsOptional')}</Label>
            <Textarea value={form.generalRecs} onChange={e => setForm(f => ({ ...f, generalRecs: e.target.value }))} />
            <Label>{t('reports.form.notesOptional')}</Label>
            <Textarea value={form.generalNotes} onChange={e => setForm(f => ({ ...f, generalNotes: e.target.value }))} />
          </>}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Btn onClick={createReport}>{t('reports.form.save')}</Btn>
            <Btn variant="ghost" onClick={() => setShowCreate(false)}>{t('button.cancel.text')}</Btn>
          </div>
        </FormCard>
      )}

      {/* ── Filters ── */}
      <Toolbar>
        <Select value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">{t('reports.filters.allTypes')}</option>
          {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.emoji} {typeLabel(k as ReportType)}</option>)}
        </Select>
        <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">{t('reports.filters.allStatuses')}</option>
          {Object.keys(STATUS_META).map(k => <option key={k} value={k}>{statusLabel(k as ReportStatus)}</option>)}
        </Select>
        <Select value={filterYear} onChange={e => setFilterYear(e.target.value)}>
          {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </Select>
      </Toolbar>

      {/* ── Table ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>{t('common.loading')}</div>
      ) : reports.length === 0 ? (
        <Empty>{t('reports.empty')}<br /><span style={{ fontSize: 13 }}>{t('reports.emptyHint')}</span></Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('form.label.type')}</Th>
              <Th>{t('reports.table.title')}</Th>
              <Th>{t('reports.table.yards')}</Th>
              <Th>{t('form.label.date')}</Th>
              <Th>{t('reports.table.status')}</Th>
              <Th>{t('reports.table.signedBy')}</Th>
              <Th>{t('common.actions')}</Th>
            </tr>
          </thead>
          <tbody>
            {reports.map(r => {
              const tm = TYPE_META[r.type]
              const sm = STATUS_META[r.status]
              return (
                <Tr key={r._id} onClick={() => openReport(r)}>
                  <Td><Badge color={tm.color}>{tm.emoji} {typeLabel(r.type)}</Badge></Td>
                  <Td style={{ fontWeight: 600 }}>{r.title}</Td>
                  <Td style={{ color: '#6b7280' }}>{(r.yardNames || []).join(', ') || '—'}</Td>
                  <Td>{fmtDate(r.date)}</Td>
                  <Td><Badge color={sm.color}>{statusLabel(r.status)}</Badge></Td>
                  <Td style={{ color: '#6b7280', fontSize: 13 }}>
                    {r.signerName ? `${r.signerName} ${r.signerLastName || ''} · ${r.signerRole || ''}` : '—'}
                  </Td>
                  <Td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {r.status === 'draft' && (
                        <Btn variant="ghost" style={{ fontSize: 12, padding: '5px 10px' }} onClick={e => requestSignature(r._id, e)}>
                          📤 {t('reports.sendForSignature')}
                        </Btn>
                      )}
                      {r.status === 'pending_signature' && (
                        <CopyBtn onClick={e => copySignLink(r, e)}>📋 {t('reports.copyLink')}</CopyBtn>
                      )}
                      <Btn variant="danger" style={{ fontSize: 12, padding: '5px 10px' }} onClick={e => deleteReport(r._id, e)}>🗑</Btn>
                    </div>
                  </Td>
                </Tr>
              )
            })}
          </tbody>
        </Table>
      )}

      {/* ── Detail Modal ── */}
      {selected && (
        <Overlay onClick={() => setSelected(null)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <Badge color={TYPE_META[selected.type].color} style={{ marginBottom: 8 }}>
                  {TYPE_META[selected.type].emoji} {typeLabel(selected.type)}
                </Badge>
                <ModalTitle>{selected.title}</ModalTitle>
              </div>
              <CloseBtn onClick={() => setSelected(null)}>×</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <Row><RL>{t('reports.detail.programYear')}</RL><RV>{selected.programYear}</RV></Row>
              <Row><RL>{t('form.label.date')}</RL><RV>{fmtDate(selected.date)}</RV></Row>
              <Row><RL>{t('reports.table.yards')}</RL><RV>{(selected.yardNames || []).join(', ') || '—'}</RV></Row>
              <Row><RL>{t('reports.detail.preparedBy')}</RL><RV>{selected.userName}</RV></Row>
              <Row><RL>{t('reports.table.status')}</RL><RV><Badge color={STATUS_META[selected.status].color}>{statusLabel(selected.status)}</Badge></RV></Row>

              {selected.varroaData && (
                <DataBlock>
                  <strong>🔬 {t('reports.detail.varroaData')}</strong><br />
                  {t('reports.form.method')}: {methodLabel(selected.varroaData.method)} &nbsp;·&nbsp;
                  {t('reports.form.samples')}: {selected.varroaData.samplesCount} &nbsp;·&nbsp;
                  {t('reports.detail.avgInfestation')}: <strong>{selected.varroaData.averageInfestationPct}%</strong>
                  {selected.varroaData.aboveThreshold
                    ? <span style={{ color: '#ef4444', fontWeight: 700 }}> ⚠️ {t('reports.detail.aboveThreshold', { threshold: selected.varroaData.threshold })}</span>
                    : <span style={{ color: '#10b981' }}> ✅ {t('reports.detail.belowThreshold', { threshold: selected.varroaData.threshold })}</span>}
                  {selected.varroaData.notes && <><br />{t('form.label.notes')}: {selected.varroaData.notes}</>}
                </DataBlock>
              )}

              {selected.generalData && (
                <DataBlock>
                  {selected.generalData.vetLicenseNumber && <><strong>{t('reports.detail.vetLicense')}:</strong> {selected.generalData.vetLicenseNumber}<br /></>}
                  <strong>{t('reports.detail.findingsDiagnosis')}:</strong> {selected.generalData.findings || '—'}<br />
                  {selected.generalData.causeOfLoss && <><strong>{t('reports.detail.causeOfLoss')}:</strong> {selected.generalData.causeOfLoss}<br /></>}
                  {(selected.generalData.coloniesInspected != null || selected.generalData.coloniesLost != null) && (
                    <><strong>{t('reports.detail.colonies')}:</strong> {t('reports.detail.coloniesSummary', { inspected: selected.generalData.coloniesInspected ?? '—', lost: selected.generalData.coloniesLost ?? '—' })}<br /></>
                  )}
                  <strong>{t('reports.detail.recommendations')}:</strong> {selected.generalData.recommendations || '—'}
                  {selected.generalData.notes && <><br />{t('form.label.notes')}: {selected.generalData.notes}</>}
                </DataBlock>
              )}

              {selected.status === 'signed' && (
                <>
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                    <strong style={{ fontSize: 15 }}>✅ {t('reports.detail.signature')}</strong>
                    <Row style={{ marginTop: 8 }}><RL>{t('reports.table.signedBy')}</RL><RV>{selected.signerName} {selected.signerLastName}</RV></Row>
                    <Row><RL>{t('reports.detail.role')}</RL><RV>{selected.signerRole}</RV></Row>
                    <Row><RL>{t('reports.detail.signedAt')}</RL><RV>{fmtDate(selected.signedAt || '')}</RV></Row>
                    {selected.signatureDataUrl && <SigImg src={selected.signatureDataUrl} alt={t('reports.detail.signature')} />}
                  </div>
                  {(selected.type === 'general' || selected.type === 'loss_summary') && (
                    <div style={{ marginTop: 14, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', padding: '10px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                      📋 {t('reports.detail.readyForElap')}
                    </div>
                  )}
                </>
              )}

              {(selected.status === 'draft' || selected.status === 'pending_signature') && (
                <div style={{ marginTop: 20 }}>
                  <Btn onClick={async e => { await requestSignature(selected._id, e as any); setSelected(null) }}>
                    📤 {selected.status === 'draft' ? t('reports.sendForSignature') : t('reports.copySignLinkAgain')}
                  </Btn>
                </div>
              )}
            </ModalBody>
          </ModalBox>
        </Overlay>
      )}
    </Page>
  )
}
